// Robust Razorpay payment service with proper Firebase integration
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/config';
import { getRazorpayKeyId, getRazorpayKeyIdSync } from '../utils/razorpayConfig';
import analyticsService from './analyticsService';

const functions = getFunctions(app, 'asia-south1');

// Get Razorpay key (will use Firestore if available, fallback to hardcoded)
// For synchronous access, use cached value
let RAZORPAY_KEY = getRazorpayKeyIdSync();

// Preload key from Firestore on module load
getRazorpayKeyId().then(keyId => {
  RAZORPAY_KEY = keyId;
  console.log('✅ Razorpay key loaded:', keyId);
}).catch(err => {
  console.warn('⚠️ Failed to load Razorpay key from Firestore, using fallback');
});

// Load Razorpay script dynamically
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

// Create a real Razorpay order using Firebase Functions
export const createRazorpayOrder = async (amount) => {
  try {
    console.log('Creating Razorpay order for amount:', amount, 'Type:', typeof amount);
    
    // Validate amount before sending to Firebase
    if (!amount || amount <= 0 || isNaN(amount)) {
      throw new Error(`Invalid amount provided: ${amount} (type: ${typeof amount})`);
    }
    
    const createPaymentOrder = httpsCallable(functions, 'createPaymentOrder');
    const result = await createPaymentOrder({ 
      amount: Number(amount), // Ensure it's a number
      currency: 'INR'
    });
    
    console.log('Razorpay order created:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('Error creating Razorpay order:', error);
    throw new Error('Failed to create payment order: ' + error.message);
  }
};

// Verify payment using Firebase Functions
export const verifyRazorpayPayment = async (paymentData) => {
  try {
    console.log('Verifying payment with asia-south1 region:', paymentData);
    console.log('Functions region configured for:', functions.app.options.projectId, 'region: asia-south1');
    
    const verifyPayment = httpsCallable(functions, 'verifyPayment');
    console.log('Calling verifyPayment function...');
    const result = await verifyPayment(paymentData);
    console.log('verifyPayment function call completed');
    
    console.log('Payment verification result:', result.data);
    
    // Handle the response structure properly
    if (result.data && typeof result.data === 'object') {
    return result.data;
    } else {
      console.error('Invalid verification response structure:', result);
      return {
        success: false,
        verified: false,
        error: 'Invalid verification response'
      };
    }
    
  } catch (error) {
    console.error('Error verifying payment:', error);
    
    // Return a structured error response instead of throwing
    return {
      success: false,
      verified: false,
      error: error.message || 'Payment verification failed'
    };
  }
};

// Create payment options for Razorpay
export const createPaymentOptions = async (orderData, user, orderDetails = {}) => {
  // Get the latest key from Firestore (or use cached value)
  const keyId = await getRazorpayKeyId();
  // Use the key returned from backend to ensure it matches the created order
  const resolvedKey = orderData.key || keyId;

  return {
    key: resolvedKey,
    amount: orderData.amount,
    currency: orderData.currency,
    name: 'Petzify',
    description: orderDetails.description || 'Order Payment',
    order_id: orderData.order_id, // Real Razorpay order ID
    prefill: {
      name: user?.name || user?.displayName || '',
      email: user?.email || '',
      contact: user?.phone || ''
    },
    theme: {
      color: '#3399cc'
    },
    modal: {
      escape: false,
      ondismiss: function() {
        console.log('Payment modal closed by user');
      }
    }
  };
};

// Process payment with Razorpay
export const processPayment = async (paymentOptions) => {
  try {
    // Ensure Razorpay is loaded
    const isRazorpayLoaded = await loadRazorpayScript();
    
    if (!isRazorpayLoaded) {
      throw new Error('Unable to load payment gateway. Please check your internet connection.');
    }

    return new Promise((resolve, reject) => {
      // Log critical options to help diagnose merchant/key/order issues
      try {
        console.log('Opening Razorpay with:', {
          key: paymentOptions.key,
          order_id: paymentOptions.order_id,
          amount: paymentOptions.amount,
          currency: paymentOptions.currency
        });
      } catch (_) {
        // ignore logging errors
      }

      const rzp = new window.Razorpay({
        ...paymentOptions,
        handler: async function(response) {
          try {
            console.log('Payment successful:', response);
            
            // Verify payment with Firebase Functions
            const verificationResult = await verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            console.log('Verification result received:', verificationResult);
            
            // Check if verification was successful
            if (verificationResult && verificationResult.verified === true) {
              console.log('Payment verification successful');
              
              // Track purchase event
              const transactionId = response.razorpay_payment_id;
              const value = paymentOptions.amount / 100; // Convert from paise to rupees
              const items = paymentOptions.orderDetails?.items || [];
              
              analyticsService.logPurchase(transactionId, value, 'INR', items);
              
              resolve({
                success: true,
                verified: true,
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature
              });
            } else {
              console.error('Payment verification failed:', verificationResult);
              const errorMsg = verificationResult?.error || 'Payment verification failed';
              reject(new Error(errorMsg));
            }
            
          } catch (error) {
            console.error('Payment verification error:', error);
            reject(new Error('Payment verification failed: ' + error.message));
          }
        },
        modal: {
          ...paymentOptions.modal,
          ondismiss: function() {
            console.log('Payment cancelled by user');
            reject(new Error('Payment cancelled by user'));
          }
        }
      });

      rzp.on('payment.failed', function(response) {
        try {
          console.error('Payment failed:', response);
        } catch (_) {}
        const message = response?.error?.description || response?.error?.reason || 'Payment failed';
        reject(new Error(message));
      });

      // Also catch modal dismissal which often happens when Razorpay shows a merchant error
      rzp.open();
    });

  } catch (error) {
    console.error('Payment processing error:', error);
    throw error;
  }
};

// Main payment function - simplified interface
export const initiatePayment = async (amount, user, orderDetails = {}, existingOrder = null) => {
  try {
    console.log('Initiating payment for amount:', amount, 'User:', user?.email);
    
    // Step 1: Use existing order or create new one
    const orderData = existingOrder || await createRazorpayOrder(amount);
    console.log('Order ready:', orderData.order_id, 'Using existing:', !!existingOrder);
    
    // Step 2: Create payment options (now async to get key from Firestore)
    const paymentOptions = await createPaymentOptions(orderData, user, orderDetails);
    console.log('Payment options created');
    
    // Step 3: Process payment
    const result = await processPayment(paymentOptions);
    console.log('Payment processing completed:', result);
    
    return result;
    
  } catch (error) {
    console.error('Payment initiation failed:', error);
    throw error;
  }
}; 